/* eslint-disable no-undef */
/**
 * PrelanderCompleteModern - Gamified completion animation for research type
 * Part of Tutkimuskeskus 2026 Design System
 * 
 * Features:
 * - Floating data particles that converge to center
 * - Step-by-step progression with satisfying checkmarks
 * - Progress ring with smooth fill animation
 * - Professional but engaging aesthetic
 */

import React, { useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box } from '@material-ui/core'
import { motion, AnimatePresence } from 'framer-motion'

import Terms from '../views/partials/Terms'
import QuestionPageFooter from '../views/partials/QuestionPageFooter'

// Import design system CSS
import '../styles/design-system.css'

const useStyles = makeStyles(theme => ({
    wrapper: {
        fontFamily: '"Plus Jakarta Sans", var(--tk-font-family), system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '48px 24px 24px 24px',
        textAlign: 'center',
        minHeight: 420,
        position: 'relative',
        overflow: 'hidden',
        [theme.breakpoints.down(600)]: {
            padding: '32px 16px 16px 16px',
            minHeight: 380,
        },
    },
    
    // Floating particles container
    particlesContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
    },
    
    particle: {
        position: 'absolute',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #205a40 0%, #3d8b68 100%)',
        opacity: 0.5,
    },
    
    // Main icon area
    iconArea: {
        position: 'relative',
        marginBottom: 32,
        zIndex: 2,
    },
    
    iconContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    
    // Outer glow ring
    glowRing: {
        position: 'absolute',
        width: 120,
        height: 120,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(32, 90, 64, 0.12) 0%, rgba(32, 90, 64, 0) 70%)',
        [theme.breakpoints.down(600)]: {
            width: 100,
            height: 100,
        },
    },
    
    // Progress ring (SVG)
    progressRingSvg: {
        position: 'absolute',
        width: 100,
        height: 100,
        transform: 'rotate(-90deg)',
        [theme.breakpoints.down(600)]: {
            width: 85,
            height: 85,
        },
    },
    
    progressTrack: {
        fill: 'none',
        stroke: 'rgba(0, 0, 0, 0.08)',
        strokeWidth: 4,
    },
    
    progressFill: {
        fill: 'none',
        stroke: '#205a40',
        strokeWidth: 4,
        strokeLinecap: 'round',
        transition: 'stroke-dashoffset 0.15s ease-out',
    },
    
    // Main icon circle
    iconCircle: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'linear-gradient(145deg, #2d7a58 0%, #205a40 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(32, 90, 64, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        zIndex: 3,
        [theme.breakpoints.down(600)]: {
            width: 60,
            height: 60,
        },
    },
    
    iconSvg: {
        width: 32,
        height: 32,
        color: 'white',
        [theme.breakpoints.down(600)]: {
            width: 28,
            height: 28,
        },
    },
    
    // Title
    title: {
        fontSize: '1.75rem',
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: 8,
        letterSpacing: '-0.02em',
        zIndex: 2,
        [theme.breakpoints.down(600)]: {
            fontSize: '1.5rem',
        },
    },
    
    subtitle: {
        fontSize: '1rem',
        fontWeight: 500,
        color: '#666',
        marginBottom: 32,
        zIndex: 2,
        [theme.breakpoints.down(600)]: {
            fontSize: '0.9rem',
            marginBottom: 24,
        },
    },
    
    // Steps container
    stepsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 32,
        zIndex: 2,
        width: '100%',
        maxWidth: 320,
        [theme.breakpoints.down(600)]: {
            gap: 12,
            marginBottom: 24,
        },
    },
    
    stepItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
    },
    
    stepIconWrapper: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
    },
    
    stepIconPending: {
        background: 'rgba(0, 0, 0, 0.06)',
        border: '2px solid rgba(0, 0, 0, 0.1)',
    },
    
    stepIconProcessing: {
        background: 'linear-gradient(145deg, #e8f2ed 0%, #d4e8dc 100%)',
        border: '2px solid #5a9a7a',
    },
    
    stepIconCompleted: {
        background: 'linear-gradient(145deg, #2d7a58 0%, #205a40 100%)',
        boxShadow: '0 2px 8px rgba(32, 90, 64, 0.35)',
    },
    
    stepText: {
        fontSize: '1.05rem',
        fontWeight: 500,
        color: '#333',
        textAlign: 'left',
        transition: 'color 0.3s ease',
        [theme.breakpoints.down(600)]: {
            fontSize: '0.95rem',
        },
    },
    
    stepTextCompleted: {
        color: '#205a40',
    },
    
    stepTextProcessing: {
        color: '#333',
    },
    
    stepTextPending: {
        color: '#999',
    },
    
    // Footer
    footerWrapper: {
        marginTop: 'auto',
        paddingTop: 24,
        width: '100%',
        zIndex: 2,
    },
}))

// Animated check icon with draw effect
const AnimatedCheckIcon = () => (
    <motion.svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="white" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round"
    >
        <motion.polyline 
            points="20 6 9 17 4 12"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
        />
    </motion.svg>
)

// Spinner icon for processing state
// We rotate a wrapper div instead of the SVG to ensure proper center rotation
const SpinnerIcon = ({ color = '#205a40' }) => (
    <motion.div
        style={{ 
            width: 16, 
            height: 16, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
        }}
        animate={{ rotate: -360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    >
        <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
        >
            <path d="M12 3a9 9 0 0 1 6.219 15.56" />
        </svg>
    </motion.div>
)

// Sparkles icon - modern, exciting, gamified feel
const SparklesIcon = () => (
    <svg 
        width="100%" 
        height="100%" 
        viewBox="0 0 24 24" 
        fill="currentColor"
    >
        {/* Main large sparkle */}
        <path d="M12 2L13.09 8.26L18 6L15.74 10.91L22 12L15.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L8.26 13.09L2 12L8.26 10.91L6 6L10.91 8.26L12 2Z" />
        {/* Small sparkle top-right */}
        <circle cx="19" cy="5" r="1.5" />
        {/* Small sparkle bottom-left */}
        <circle cx="5" cy="19" r="1" />
    </svg>
)

// Floating particle component
const FloatingParticle = ({ delay, duration, startX, startY, size }) => {
    const classes = useStyles()
    
    return (
        <motion.div
            className={classes.particle}
            style={{
                width: size,
                height: size,
                left: startX,
                top: startY,
            }}
            initial={{ 
                opacity: 0, 
                scale: 0,
                x: 0,
                y: 0,
            }}
            animate={{ 
                opacity: [0, 0.7, 0.7, 0],
                scale: [0, 1, 1, 0.5],
                x: [`0%`, `${(50 - startX) * 0.7}%`],
                y: [`0%`, `${(40 - startY) * 0.7}%`],
            }}
            transition={{
                duration: duration * 2,
                delay: delay * 2,
                ease: 'easeInOut',
            }}
        />
    )
}

// Generate random particles - memoized to prevent regeneration
const generateParticles = (count) => {
    const particles = []
    for (let i = 0; i < count; i++) {
        particles.push({
            id: i,
            delay: Math.random() * 3,
            duration: 2 + Math.random() * 2,
            startX: Math.random() * 100,
            startY: Math.random() * 100,
            size: 4 + Math.random() * 8,
        })
    }
    return particles
}

const PrelanderCompleteModern = ({ prelander, questionPage, theme, layout }) => {
    const classes = useStyles()
    const [progress, setProgress] = useState(0)
    const [currentStep, setCurrentStep] = useState(0)
    
    // Memoize particles so they don't regenerate on re-render
    const particles = useMemo(() => generateParticles(12), [])
    
    // Step definitions
    const steps = [
        { id: 0, text: 'Vastaukset vastaanotettu', processingText: 'Vastaanotetaan vastauksia...' },
        { id: 1, text: 'Tiedot prosessoitu', processingText: 'Prosessoidaan tietoja...' },
        { id: 2, text: 'Vielä muutama kysymys...', processingText: 'Vielä muutama kysymys...', isLast: true },
    ]
    
    // Total duration: ~12 seconds for satisfying experience
    const TOTAL_DURATION = 12000
    const STEP_DELAYS = [1600, 5000, 9000] // When each step completes
    
    useEffect(() => {
        const startTime = Date.now()
        
        // Smooth progress animation
        const updateProgress = () => {
            const elapsed = Date.now() - startTime
            const newProgress = Math.min((elapsed / TOTAL_DURATION) * 100, 100)
            setProgress(newProgress)
            
            if (elapsed < TOTAL_DURATION) {
                requestAnimationFrame(updateProgress)
            }
        }
        
        requestAnimationFrame(updateProgress)
        
        // Step progression timers
        const timers = STEP_DELAYS.map((delay, index) => 
            setTimeout(() => setCurrentStep(index + 1), delay)
        )
        
        return () => timers.forEach(timer => clearTimeout(timer))
    }, [])
    
    // Progress ring calculations
    const radius = 46
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (progress / 100) * circumference
    
    // Get step state
    const getStepState = (stepIndex) => {
        if (stepIndex < currentStep) return 'completed'
        if (stepIndex === currentStep) return 'processing'
        return 'pending'
    }
    
    return (
        <Box className={classes.wrapper}>
            {/* Floating particles background */}
            <div className={classes.particlesContainer}>
                {particles.map(particle => (
                    <FloatingParticle key={particle.id} {...particle} />
                ))}
            </div>
            
            {/* Main icon with progress ring */}
            <motion.div 
                className={classes.iconArea}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.1 }}
            >
                <div className={classes.iconContainer}>
                    {/* Pulsing glow */}
                    <motion.div 
                        className={classes.glowRing}
                        animate={{ 
                            scale: [1, 1.2, 1],
                            opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    />
                    
                    {/* Progress ring SVG */}
                    <svg className={classes.progressRingSvg} viewBox="0 0 100 100">
                        <circle
                            className={classes.progressTrack}
                            cx="50"
                            cy="50"
                            r={radius}
                        />
                        <circle
                            className={classes.progressFill}
                            cx="50"
                            cy="50"
                            r={radius}
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                        />
                    </svg>
                    
                    {/* Main icon */}
                    <motion.div 
                        className={classes.iconCircle}
                        animate={{ 
                            scale: [1, 1.05, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    >
                    <div className={classes.iconSvg}>
                        <SparklesIcon />
                    </div>
                    </motion.div>
                </div>
            </motion.div>
            
            {/* Title */}
            <motion.h2 
                className={classes.title}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
            >
                Kiitos vastauksistasi!
            </motion.h2>
            
            {/* Subtitle */}
            <motion.p 
                className={classes.subtitle}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
            >
                Käsittelemme vastauksiasi...
            </motion.p>
            
            {/* Steps */}
            <div className={classes.stepsContainer}>
                {steps.map((step, index) => {
                    const state = getStepState(index)
                    
                    return (
                        <motion.div
                            key={step.id}
                            className={classes.stepItem}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + index * 0.15, duration: 0.4 }}
                        >
                            {/* Step icon */}
                            <motion.div 
                                className={`${classes.stepIconWrapper} ${
                                    state === 'completed' ? classes.stepIconCompleted :
                                    state === 'processing' ? classes.stepIconProcessing :
                                    classes.stepIconPending
                                }`}
                                animate={state === 'completed' ? { scale: [1, 1.15, 1] } : {}}
                                transition={{ duration: 0.3 }}
                            >
                                <AnimatePresence mode="wait">
                                    {state === 'completed' && (
                                        <motion.div
                                            key="check"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ 
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center' 
                                            }}
                                        >
                                            <AnimatedCheckIcon />
                                        </motion.div>
                                    )}
                                    {state === 'processing' && (
                                        <motion.div
                                            key="spinner"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ 
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center' 
                                            }}
                                        >
                                            <SpinnerIcon color="#205a40" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                            
                            {/* Step text */}
                            <motion.span 
                                className={`${classes.stepText} ${
                                    state === 'completed' ? classes.stepTextCompleted :
                                    state === 'processing' ? classes.stepTextProcessing :
                                    classes.stepTextPending
                                }`}
                            >
                                {state === 'processing' && !step.isLast 
                                    ? step.processingText 
                                    : step.text
                                }
                            </motion.span>
                        </motion.div>
                    )
                })}
            </div>
            
            {/* Footer */}
            <Box className={classes.footerWrapper}>
                {questionPage?.showFooter && <QuestionPageFooter layout={layout} />}
                {questionPage?.termsPosition === 'footer' && (
                    <Terms domain={questionPage.domain} theme={theme} layout={layout} />
                )}
            </Box>
        </Box>
    )
}

export default PrelanderCompleteModern
